import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { runAssetSelfTest, type AssetSelfTestResult } from "@/lib/asset-self-test";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Activity, AlertCircle, CheckCircle2, RefreshCw, ShieldAlert } from "lucide-react";

type Search = { from?: string };

const STORAGE_KEY = "troubleshooting:last-self-test";

type Persisted = {
  result: AssetSelfTestResult;
  timestamp: number;
};

function readPersisted(): Persisted | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Persisted;
    if (
      !parsed ||
      typeof parsed.timestamp !== "number" ||
      !parsed.result ||
      !Array.isArray(parsed.result.probes)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function readPersistedResult(): AssetSelfTestResult | null {
  return readPersisted()?.result ?? null;
}

function readPersistedTimestamp(): number | null {
  return readPersisted()?.timestamp ?? null;
}

function persistResult(result: AssetSelfTestResult, timestamp: number): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ result, timestamp } satisfies Persisted),
    );
  } catch {
    /* Quota / privacy mode → ignore */
  }
}

export const Route = createFileRoute("/troubleshooting")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    from: typeof search.from === "string" ? search.from : undefined,
  }),
  component: TroubleshootingPage,
  head: () => ({
    meta: [{ title: "Troubleshooting — InboxPulse" }],
  }),
});

function TroubleshootingPage() {
  const { from } = useSearch({ from: "/troubleshooting" });
  const [result, setResult] = useState<AssetSelfTestResult | null>(() => readPersistedResult());
  const [busy, setBusy] = useState(false);
  const [intervalSec, setIntervalSec] = useState<number>(5);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [lastRunAt, setLastRunAt] = useState<number | null>(() => readPersistedTimestamp());
  const [countdown, setCountdown] = useState<number>(5);
  const [manualRunAt, setManualRunAt] = useState<number | null>(null);
  const busyRef = useRef(false);

  const rerun = useCallback(async (opts?: { manual?: boolean }) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    if (opts?.manual) setManualRunAt(Date.now());
    try {
      const r = await runAssetSelfTest();
      setResult(r);
      const ts = Date.now();
      setLastRunAt(ts);
      persistResult(r, ts);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }, []);

  // Initial-Lauf
  useEffect(() => {
    void rerun();
  }, [rerun]);

  // Auto-Refresh-Loop: setInterval wird sauber pro Intervall/Toggle neu
  // aufgesetzt. Tab im Hintergrund (visibilityState === "hidden") pausiert,
  // damit wir keine sinnlosen Probes feuern.
  useEffect(() => {
    if (!autoRefresh) return;
    if (typeof window === "undefined") return;
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      void rerun();
    };
    const id = window.setInterval(tick, intervalSec * 1000);
    return () => window.clearInterval(id);
  }, [autoRefresh, intervalSec, rerun]);

  // Countdown-Anzeige (1 Hz). Reset bei jedem abgeschlossenen Lauf.
  useEffect(() => {
    if (!autoRefresh) {
      setCountdown(intervalSec);
      return;
    }
    setCountdown(intervalSec);
    if (typeof window === "undefined") return;
    const id = window.setInterval(() => {
      setCountdown((c) => (c <= 1 ? intervalSec : c - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [autoRefresh, intervalSec, lastRunAt]);

  // Live-Status: solange noch kein Re-Check lief, gilt der ursprüngliche
  // Verdacht. Sobald ein Lauf vorliegt, entscheidet er — alles ok → grüner
  // Cleared-State, sonst weiterhin Warnung.
  const checked = result !== null;
  const cleared = checked && result.totalCount > 0 && result.failedCount === 0;
  const stillBlocked =
    checked && result.totalCount >= 2 && result.failedCount === result.totalCount;
  const failedProbes = result?.probes.filter((p) => !p.ok) ?? [];

  // Klassifiziert die Fehlermeldung des Browsers in eine knappe, lesbare
  // Ursache. Browser sind hier absichtlich vage (Privacy), daher heuristisch.
  function classifyError(
    err: string | null,
    status: number | null,
  ): {
    label: string;
    hint: string;
  } {
    const msg = (err ?? "").toLowerCase();
    if (!err && status && status >= 400) {
      return {
        label: `HTTP ${status}`,
        hint: "Server hat geantwortet, aber mit Fehlerstatus.",
      };
    }
    if (msg.includes("abort")) {
      return {
        label: "Timeout / Abbruch",
        hint: "Anfrage wurde nach 3s abgebrochen — Netzwerk sehr langsam oder hängt.",
      };
    }
    if (
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("load failed") ||
      msg.includes("err_blocked") ||
      msg.includes("err_failed")
    ) {
      return {
        label: "Blockiert / Netzwerk-Cancel",
        hint: "Browser oder Extension (AdBlocker, Brave Shields, Antivirus) hat die Anfrage gestoppt.",
      };
    }
    if (msg.includes("cors")) {
      return {
        label: "CORS-Fehler",
        hint: "Server liefert keine passenden CORS-Header.",
      };
    }
    return {
      label: err ?? "Unbekannt",
      hint: "Keine eindeutige Ursache erkennbar.",
    };
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[radial-gradient(ellipse_at_top,_var(--primary)/0.15,_transparent_50%)]">
      <div className="w-full max-w-2xl">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="size-9 rounded-lg bg-[image:var(--gradient-primary)] flex items-center justify-center shadow-[var(--shadow-glow)]">
            <Activity className="size-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-semibold tracking-tight">InboxPulse</span>
        </Link>

        <Card className="p-8 bg-card/80 backdrop-blur border-border">
          {cleared ? (
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle2 className="size-6 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <h1 className="text-2xl font-semibold">Alle Assets erreichbar</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Der erneute Selbsttest hat alle {result!.totalCount} Probe
                  {result!.totalCount === 1 ? "" : "n"} erfolgreich geladen — die App sollte jetzt
                  wieder funktionieren. Du kannst zurück zum Login gehen.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 mb-4">
              <ShieldAlert
                className={`size-6 shrink-0 mt-0.5 ${
                  stillBlocked ? "text-red-500" : "text-amber-500"
                }`}
              />
              <div>
                <h1 className="text-2xl font-semibold">
                  {stillBlocked ? "Asset-Ladung fehlgeschlagen" : "Asset-Ladung wird geprüft"}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {stillBlocked
                    ? `Auch der erneute Selbsttest konnte ${result!.failedCount}/${result!.totalCount} App-Ressourcen nicht laden. Das deutet stark auf einen AdBlocker, Privacy-Extension oder Brave Shields hin.`
                    : `Der automatische Selbsttest${from ? ` von ${from}` : ""} hatte zuvor App-Ressourcen nicht laden können. Wir prüfen den Live-Status erneut.`}
                </p>
              </div>
            </div>
          )}

          {!cleared && (
            <div className="mt-6 space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                So behebst du es
              </h2>
              <ol className="list-decimal pl-5 text-sm space-y-2">
                <li>
                  Klicke in der Adressleiste auf das Schutzschild-Symbol (Brave-Löwe, uBlock,
                  AdBlock, Ghostery) und deaktiviere es für diese Domain.
                </li>
                <li>
                  Falls dein Browser die Seite automatisch übersetzt: klicke auf das
                  Übersetzungs-Symbol und wähle "Original anzeigen".
                </li>
                <li>
                  Erzwinge ein hartes Neuladen mit{" "}
                  <kbd className="px-1.5 py-0.5 text-xs rounded bg-muted">Strg/Cmd+Shift+R</kbd>.
                </li>
                <li>
                  Teste die Seite testweise im Inkognito-Fenster oder einem anderen Browser —
                  funktioniert es dort, ist eine Extension die Ursache.
                </li>
              </ol>
            </div>
          )}

          {result && (
            <>
              {failedProbes.length > 0 && (
                <div className="mt-6 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="size-4 text-red-500" />
                    <h2 className="text-sm font-semibold">
                      Fehlgeschlagene Assets ({failedProbes.length}/{result.totalCount})
                    </h2>
                  </div>
                  <ul className="space-y-3">
                    {failedProbes.map((p) => {
                      const c = classifyError(p.error, p.status);
                      return (
                        <li key={p.url} className="text-xs space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center rounded-full bg-red-500/15 text-red-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                              {c.label}
                            </span>
                            <span className="text-muted-foreground">
                              {Math.round(p.durationMs)} ms
                              {p.status !== null && ` · Status ${p.status}`}
                            </span>
                          </div>
                          <div className="font-mono text-[11px] break-all text-foreground/90">
                            {p.url}
                          </div>
                          <div className="text-muted-foreground">
                            {c.hint}
                            {p.error && p.error !== c.label && (
                              <span className="ml-1 opacity-70">({p.error})</span>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <details className="mt-6 text-xs" open>
                <summary className="cursor-pointer font-semibold">
                  Selbsttest-Ergebnis ({result.failedCount}/{result.totalCount} fehlgeschlagen ·{" "}
                  {result.mode}
                  {lastRunAt ? ` · ${new Date(lastRunAt).toLocaleTimeString()}` : ""})
                </summary>
                <ul className="mt-2 space-y-1 font-mono text-[11px]">
                  {result.probes.map((p) => (
                    <li key={p.url} className={p.ok ? "text-emerald-500" : "text-red-500"}>
                      {p.ok ? "✓" : "✗"} {p.status ?? "—"} {Math.round(p.durationMs)}ms{" "}
                      <span className="break-all">{p.url}</span>
                      {p.error && <span className="ml-2 opacity-70">({p.error})</span>}
                    </li>
                  ))}
                </ul>
              </details>
            </>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="size-3.5 accent-primary"
              />
              <span>Auto-Refresh</span>
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">alle</span>
              <select
                value={intervalSec}
                onChange={(e) => setIntervalSec(Number(e.target.value))}
                disabled={!autoRefresh}
                className="bg-background border border-border rounded px-1.5 py-0.5 text-xs disabled:opacity-50"
              >
                <option value={2}>2 s</option>
                <option value={5}>5 s</option>
                <option value={10}>10 s</option>
                <option value={30}>30 s</option>
                <option value={60}>60 s</option>
              </select>
            </div>
            {autoRefresh && (
              <span className="text-muted-foreground">
                {busy ? "Prüfe…" : `Nächster Check in ${countdown}s`}
              </span>
            )}
          </div>

          {/* Status-Banner für manuelle Re-Checks: bestätigt sichtbar, dass
              der Klick ausgeführt wurde und fasst das Ergebnis zusammen. */}
          {(busy || manualRunAt !== null) && (
            <div
              role="status"
              aria-live="polite"
              className={`mt-6 rounded-lg border p-3 text-sm flex items-start gap-2 ${
                busy
                  ? "border-primary/30 bg-primary/5 text-foreground"
                  : cleared
                    ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300"
                    : stillBlocked
                      ? "border-red-500/30 bg-red-500/5 text-red-300"
                      : "border-amber-500/30 bg-amber-500/5 text-amber-200"
              }`}
            >
              {busy ? (
                <RefreshCw className="size-4 mt-0.5 shrink-0 animate-spin text-primary" />
              ) : cleared ? (
                <CheckCircle2 className="size-4 mt-0.5 shrink-0" />
              ) : (
                <AlertCircle className="size-4 mt-0.5 shrink-0" />
              )}
              <span>
                {busy
                  ? "Selbsttest läuft — App-Ressourcen werden gerade geladen…"
                  : cleared
                    ? `Alles erreichbar: ${result!.totalCount}/${result!.totalCount} Assets geladen (geprüft um ${new Date(lastRunAt!).toLocaleTimeString()}).`
                    : stillBlocked
                      ? `Weiterhin blockiert: ${result!.failedCount}/${result!.totalCount} Assets fehlgeschlagen (geprüft um ${new Date(lastRunAt!).toLocaleTimeString()}). Prüfe die Liste oben.`
                      : result && result.totalCount > 0
                        ? `Teilweise erreichbar: ${result.failedCount}/${result.totalCount} Assets fehlgeschlagen (geprüft um ${new Date(lastRunAt!).toLocaleTimeString()}).`
                        : "Keine Probes gefunden — versuche einen harten Reload."}
              </span>
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2">
            <Button onClick={() => rerun({ manual: true })} disabled={busy} variant="default">
              <RefreshCw className={`size-4 mr-2 ${busy ? "animate-spin" : ""}`} />
              {busy ? "Prüfe…" : "Jetzt erneut prüfen"}
            </Button>
            <Button asChild variant="outline">
              <Link to="/login">Zurück zum Login</Link>
            </Button>
            <Button asChild variant="ghost">
              <a href={typeof window !== "undefined" ? window.location.origin : "/"}>
                Startseite öffnen
              </a>
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
